import { DEFAULT_MEMBERS_SORT_BY, DEFAULT_TEAM_MEMBERS_SORT_BY } from "@components/table/members/members-sort-constants";
import { compareMembersByDefaultName, compareMembersBySeats, compareMembersByStatus } from "@components/table/members/members-table-utils";
import { isGroupMember } from "@custom-types/member-type-guards";
import { UserRoleEnum } from "@custom-types/member-types";
import { FaroCoreApiUtils } from "@stellar/core-api-client";
import { EMPTY_MEMBER_SEATS_LOOKUP } from "@utils/member-seats-utils";
import { getPrettyName } from "@utils/user-utils";
import { cloneElement } from "react";
/** Returns the member entity by the member ID if it's available */
export function getMemberById({
  memberId,
  companyMembers,
  projectMembers
}) {
  const companyMember = companyMembers.find(member => member.identity === memberId);
  const projectMember = projectMembers.find(member => member.identity === memberId);

  // Give priority to the company level member entity
  return companyMember || projectMember;
}

/** Returns the get the formatted member name by the member ID if it's available */
export function getMemberNameById({
  memberId,
  companyMembers,
  projectMembers
}) {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers
  });
  const name = getPrettyName(member);
  return name !== "" ? name : undefined;
}

/** Returns the email by the member ID if it's available */
export function getMemberEmailById({
  memberId,
  companyMembers,
  projectMembers
}) {
  const member = getMemberById({
    memberId,
    companyMembers,
    projectMembers
  });
  return member?.email;
}

/** Returns a map of members accessible by id */
export function getMembersMapById(companyMembers) {
  return new Map(companyMembers.map(member => [member.identity, member]));
}

/**
 * Shapes a member of the workspace as a group member, which is what the member autocomplete lists.
 * The workspace role assigned to them is left out, since it is what tells a workspace member from a
 * group member.
 */
export function toGroupMember(member) {
  if ("userRole" in member) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars -- Left out on purpose
    const {
      userRole,
      ...memberWithoutRole
    } = member;
    return {
      ...memberWithoutRole,
      dateJoined: 0
    };
  }
  return {
    ...member,
    dateJoined: 0
  };
}

/** Returns the higher role between two role provided */
export function getHigherRole(roleA, roleB) {
  return UserRoleEnum[roleA] >= UserRoleEnum[roleB] ? roleA : roleB;
}

/**
 * Creates an export button component with standard button type if there are members and an export component is available.
 * Generic helper to avoid repeating the same pattern across multiple files.
 *
 * @param itemCount - The count of items (members, groups, etc.) to check if export should be enabled
 * @param exportComponent - The export component from bulk actions
 * @returns The cloned export component with standard button type, or undefined if no items or component
 */
export function getExportComponent(itemCount, exportComponent) {
  return itemCount > 0 && exportComponent ? cloneElement(exportComponent, {
    buttonType: "standard"
  }) : undefined;
}

/**
 * Sorts an array of members by their name or email.
 * Teams(user-group) are prioritized over individual users.
 * For individual users, if the name is available, it will be used for sorting; otherwise, the email will be used.
 *
 * @param members - The array of members to sort
 * @returns A new sorted array of members
 */
export function sortMembersByNameOrEmail(members) {
  return members.sort((a, b) => {
    const isAGroup = FaroCoreApiUtils.Member.isTeam(a);
    const isBGroup = FaroCoreApiUtils.Member.isTeam(b);
    if (isAGroup !== isBGroup) {
      return isAGroup ? -1 : 1;
    }
    const aName = a.kind === "user" && a.identity !== a.id ? a.email : a.name;
    const bName = b.kind === "user" && b.identity !== b.id ? b.email : b.name;
    return (aName ?? "").localeCompare(bName ?? "");
  });
}

/**
 * Sorts an array of members based on the provided sorting criteria.
 * @param members - The array of members to sort
 * @param sortBy - The sorting criteria
 * @param seatsLookup - The seats of the workspace, which are not part of a member but of the
 * workspace subscription. Only the workspace members table sorts by them.
 * @returns A new sorted array of members
 */
export function sortMembers(members, sortBy, seatsLookup = EMPTY_MEMBER_SEATS_LOOKUP) {
  const sorted = [...members];
  sorted.sort((a, b) => {
    switch (sortBy) {
      case "user_ascending":
      case "name_ascending":
        return compareMembersByDefaultName(a, b, "ascending");
      case "user_descending":
      case "name_descending":
        return compareMembersByDefaultName(a, b, "descending");
      case "email_ascending":
        return a.email.localeCompare(b.email);
      case "email_descending":
        return b.email.localeCompare(a.email);
      case "status_ascending":
        return compareMembersByStatus(a, b, "ascending");
      case "status_descending":
        return compareMembersByStatus(a, b, "descending");
      case "role_ascending":
        return UserRoleEnum[b.role] - UserRoleEnum[a.role];
      case "role_descending":
        return UserRoleEnum[a.role] - UserRoleEnum[b.role];
      case "joinedDate_ascending":
        if (isGroupMember(a) && isGroupMember(b)) {
          return new Date(a.dateJoined).getTime() - new Date(b.dateJoined).getTime();
        }
        return 0;
      case "joinedDate_descending":
        if (isGroupMember(a) && isGroupMember(b)) {
          return new Date(b.dateJoined).getTime() - new Date(a.dateJoined).getTime();
        }
        return 0;
      case "seats_ascending":
        return compareMembersBySeats(a, b, seatsLookup, "ascending");
      case "seats_descending":
        return compareMembersBySeats(a, b, seatsLookup, "descending");
      default:
        return 0;
    }
  });
  return sorted;
}

/** Converts a SortItem to a MembersSortBy value */
export function getMembersSortBy(sortItem) {
  const {
    field,
    sort
  } = sortItem;
  const isAscending = sort === "asc";
  switch (field) {
    case "user":
      return isAscending ? "user_ascending" : "user_descending";
    case "name":
      return isAscending ? "name_ascending" : "name_descending";
    case "email":
      return isAscending ? "email_ascending" : "email_descending";
    case "status":
      return isAscending ? "status_ascending" : "status_descending";
    case "role":
      return isAscending ? "role_ascending" : "role_descending";
    case "joinedDate":
      return isAscending ? "joinedDate_ascending" : "joinedDate_descending";
    case "seats":
      return isAscending ? "seats_ascending" : "seats_descending";
    default:
      return DEFAULT_MEMBERS_SORT_BY;
  }
}

/** Converts a SortItem to a TeamMemberSortBy value */
export function getTeamMembersSortBy(sortItem) {
  if (sortItem.field === "joinedDate") {
    return sortItem.sort === "asc" ? "oldest_first" : "newest_first";
  }
  return DEFAULT_TEAM_MEMBERS_SORT_BY;
}